// Module "itinéraire" — voir CLAUDE.md "Itinéraire / pathfinding". Pur (aucune dépendance à
// MapLibre/au DOM), testable directement, comme `commands.ts`/`ShapeStore.ts`.

import type { LngLat, Shape } from "../adapters/types";

/**
 * Point de départ/arrivée d'un itinéraire : soit une forme existante (le cas courant, une place liée
 * à une entité), soit une position brute (ex. la position géolocalisée de l'utilisateur — voir
 * `MapAdapter.getUserLocation` — qui n'est jamais une `Shape`). Les deux extrémités d'un même appel
 * à `findRoute` peuvent être de nature différente (typiquement : position utilisateur -> forme).
 */
export type RouteEndpoint =
  | { type: "shape"; shapeId: string }
  | { type: "position"; position: LngLat };

export interface FindRouteOptions {
  /**
   * Toutes les formes connues (walkable ou non) — `findRoute` ne construit PAS un réseau à partir
   * des formes `walkable` (voir `Shape.walkable`) : l'espace est traversable PAR DÉFAUT partout sur
   * le plan, y compris le vide sans aucune forme. Seules les formes NON `walkable` de `shapes`
   * (autres que `from`/`to` elles-mêmes) agissent comme des obstacles qui bloquent le tracé — une
   * forme `walkable` ne fait que se comporter comme si elle n'existait pas pour ce calcul, exactement
   * comme un espace vide.
   */
  shapes: Shape[];
  from: RouteEndpoint;
  to: RouteEndpoint;
}

export interface RouteResult {
  /**
   * Tracé de l'itinéraire (CRS canonique, lng/lat) — une ligne directe entre `from`/`to` quand rien
   * ne la bloque, sinon une ligne brisée contournant les obstacles rencontrés (voir le commentaire de
   * `findRoute`/`gridRoute`), en commençant/terminant exactement sur la position demandée quand
   * `from`/`to` est de type `"position"`.
   */
  coordinates: LngLat[];
  /**
   * UNIQUEMENT les formes de départ/d'arrivée elles-mêmes, quand l'extrémité correspondante est de
   * type `"shape"` (jamais pour une extrémité `"position"`, qui ne correspond à aucune forme) — dans
   * cet ordre (`[from, to]`, l'un ou l'autre absent). Aucune autre forme n'est jamais listée ici :
   * l'itinéraire n'a pas de notion "d'étape" intermédiaire, seulement un tracé qui contourne ce qui
   * le bloque (voir `coordinates`).
   */
  shapeIds: string[];
}

/**
 * Grille effectivement explorée par `gridRoute` pour UN appel à `findRouteDebugGrid` (voir son
 * commentaire) — jamais construite par `findRoute`/`gridRoute` normalement (coût mémoire inutile en
 * usage réel : une vraie grille peut compter des dizaines de milliers de cellules, voir
 * `GRID_MAX_TOTAL_CELLS`, la quasi-totalité neutres/sans intérêt à visualiser). Ne liste QUE les
 * cellules bloquées (`blockedCells`, un anneau fermé chacune) et celles à prix réduit
 * (`cheapCells`, avec leur poids — voir `pathPriorityWeight` — potentiellement < 1 : jamais 1, une
 * cellule neutre n'est simplement pas dans cette liste) de la marge de recherche RÉELLEMENT retenue
 * (voir CLAUDE.md/le commentaire de `findRoute` : la marge qui donne le coût pondéré le plus bas
 * quand une source d'influence est à proximité, sinon la première qui réussit).
 */
export interface PathfindingDebugGrid {
  bounds: { minX: number; minY: number; maxX: number; maxY: number };
  cellSize: number;
  blockedCells: LngLat[][];
  cheapCells: { ring: LngLat[]; weight: number }[];
}

/** Nombre de sommets du polygone régulier approximant un cercle pour le pathfinding — distinct de
 * `CIRCLE_RING_SEGMENTS` (`adapters/maplibre/imageQuad.ts`, silhouette de rendu) : cette approximation
 * n'a besoin que d'être correcte pour du test d'intersection/recouvrement, pas visuellement lisse. */
const CIRCLE_OBSTACLE_SEGMENTS = 24;

/**
 * Anneau fermé approximant le cercle défini par `[centre, point de bord]` (voir `Shape.coordinates`
 * pour `shapeKind: "circle"` : PAS un anneau, juste ces 2 points bruts) — même formule que
 * `circleRingLngLat` (`adapters/maplibre/imageQuad.ts`), dupliquée ici plutôt qu'importée : ce fichier
 * reste sans dépendance vers `adapters/maplibre/*` (voir CLAUDE.md, frontière core/adapter), et
 * `imageQuad.ts` vit sous `adapters/maplibre/` même s'il ne dépend pas lui-même de `maplibre-gl`.
 * Sans cette conversion, un cercle (toujours exactement 2 points) échouait le test `ring.length < 3`
 * de `crossesObstacle`/`cellOverlapsRing` et se comportait comme s'il n'existait pas — jamais un
 * obstacle, quel que soit `Shape.walkable`.
 */
function circleToRing(coordinates: LngLat[]): LngLat[] {
  const [center, edge] = coordinates as [LngLat, LngLat];
  const [centerLng, centerLat] = center;
  const [edgeLng, edgeLat] = edge;
  const metersPerDegLat = 111_320;
  const metersPerDegLng = 111_320 * Math.cos((centerLat * Math.PI) / 180);
  const radiusMeters = Math.hypot((edgeLng - centerLng) * metersPerDegLng, (edgeLat - centerLat) * metersPerDegLat);

  const ring: LngLat[] = [];
  for (let i = 0; i <= CIRCLE_OBSTACLE_SEGMENTS; i++) {
    const angle = (2 * Math.PI * i) / CIRCLE_OBSTACLE_SEGMENTS;
    ring.push([
      centerLng + (radiusMeters * Math.cos(angle)) / metersPerDegLng,
      centerLat + (radiusMeters * Math.sin(angle)) / metersPerDegLat,
    ]);
  }
  return ring;
}

/** Anneau fermé utilisable pour les tests d'intersection/recouvrement d'`obstacle` — développe un
 * cercle via `circleToRing` (sinon `ring.length < 3` le ferait ignorer partout, voir plus haut), sans
 * toucher aux polylignes (`shapeKind: "line"`, gérées séparément via `openSegments` à chaque appelant :
 * un anneau fermé y ajouterait à tort une arête de fermeture dernier -> premier point). */
function obstacleRing(shape: Shape): LngLat[] {
  return shape.shapeKind === "circle" ? circleToRing(shape.coordinates) : shape.coordinates;
}

function ringPoints(ring: LngLat[]): LngLat[] {
  // Le premier point == le dernier dans un anneau fermé (voir `Shape.coordinates`) — on ne veut
  // compter/tester chaque sommet qu'une fois.
  if (ring.length > 1 && ring[0]![0] === ring[ring.length - 1]![0] && ring[0]![1] === ring[ring.length - 1]![1]) {
    return ring.slice(0, -1);
  }
  return ring;
}

/**
 * Centroïde (barycentre pondéré par l'aire) d'un polygone — formule standard "shoelace", appliquée à
 * des coordonnées RELATIVES au premier sommet (`ox`/`oy` soustraits avant la somme, rajoutés au
 * résultat) plutôt qu'aux lng/lat absolus. Nécessaire en pratique, pas juste par prudence
 * théorique : en lng/lat réel (`x`/`y` de l'ordre de quelques dizaines, ex. lng≈2.5/lat≈49) pour un
 * petit stand de quelques mètres (aire signée de l'ordre de 1e-9 à 1e-10 en degrés²), les produits
 * `x0*y1`/`x1*y0` de la formule sont de l'ordre de 100 alors que leur DIFFÉRENCE (le signal utile)
 * est ~1e9 fois plus petite — la soustraction en flottant double précision perd alors le signal dans
 * l'erreur d'arrondi, donnant un centroïde qui peut tomber franchement HORS du polygone (constaté
 * directement sur `exemple-v2.json` : un stand réel donnait un centroïde environ 30% plus haut que
 * son propre sommet le plus haut). Travailler en coordonnées relatives ramène `x`/`y` à l'échelle de
 * l'aire elle-même (quelques 1e-5), annulant cette perte de précision.
 *
 * Retombe sur la simple moyenne des sommets si l'aire signée est ~nulle (polygone dégénéré, ex. les
 * 2 points d'un `shapeKind: "circle"`, qui n'est pas un vrai anneau — voir `Shape.coordinates`) : la
 * formule pondérée par l'aire divise par zéro dans ce cas.
 */
export function polygonCentroid(ring: LngLat[]): LngLat {
  const points = ringPoints(ring);
  if (points.length === 0) return [0, 0];
  if (points.length === 1) return points[0]!;

  const [ox, oy] = points[0]!;
  let area = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < points.length; i++) {
    const [px0, py0] = points[i]!;
    const [px1, py1] = points[(i + 1) % points.length]!;
    const x0 = px0 - ox;
    const y0 = py0 - oy;
    const x1 = px1 - ox;
    const y1 = py1 - oy;
    const cross = x0 * y1 - x1 * y0;
    area += cross;
    cx += (x0 + x1) * cross;
    cy += (y0 + y1) * cross;
  }
  area /= 2;
  if (Math.abs(area) < 1e-12) {
    const sum = points.reduce<[number, number]>((acc, [x, y]) => [acc[0] + x, acc[1] + y], [0, 0]);
    return [sum[0] / points.length, sum[1] / points.length];
  }
  return [ox + cx / (6 * area), oy + cy / (6 * area)];
}

/** Distance euclidienne simple sur les coordonnées lng/lat — pas de correction géodésique
 * (haversine) : à l'échelle d'un plan intérieur (quelques centaines de mètres), l'approximation
 * plane est largement suffisante, et évite d'introduire un calcul plus coûteux pour un gain de
 * précision qui ne changerait jamais le résultat d'un plus court chemin ici. */
export function distance(a: LngLat, b: LngLat): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1]);
}

function onSegment(p: LngLat, q: LngLat, r: LngLat): boolean {
  return (
    Math.min(p[0], r[0]) - 1e-12 <= q[0] &&
    q[0] <= Math.max(p[0], r[0]) + 1e-12 &&
    Math.min(p[1], r[1]) - 1e-12 <= q[1] &&
    q[1] <= Math.max(p[1], r[1]) + 1e-12
  );
}

function orientation(p: LngLat, q: LngLat, r: LngLat): number {
  const value = (q[1] - p[1]) * (r[0] - q[0]) - (q[0] - p[0]) * (r[1] - q[1]);
  if (Math.abs(value) < 1e-12) return 0;
  return value > 0 ? 1 : 2;
}

/** Test d'intersection de 2 segments — cas général (orientations) + cas dégénérés (segments
 * colinéaires qui se chevauchent, ex. deux formes dessinées bord à bord sur exactement la même
 * arête) via `onSegment`. */
function segmentsIntersect(p1: LngLat, p2: LngLat, p3: LngLat, p4: LngLat): boolean {
  const o1 = orientation(p1, p2, p3);
  const o2 = orientation(p1, p2, p4);
  const o3 = orientation(p3, p4, p1);
  const o4 = orientation(p3, p4, p2);

  if (o1 !== o2 && o3 !== o4) return true;

  if (o1 === 0 && onSegment(p1, p3, p2)) return true;
  if (o2 === 0 && onSegment(p1, p4, p2)) return true;
  if (o3 === 0 && onSegment(p3, p1, p4)) return true;
  if (o4 === 0 && onSegment(p3, p2, p4)) return true;

  return false;
}

/** Test point-dans-polygone par lancer de rayon (ray casting) — sur le bord inclus dans une
 * tolérance flottante minime (voir `segmentsIntersect`/`onSegment`, même esprit). */
export function pointInPolygon(point: LngLat, ring: LngLat[]): boolean {
  const points = ringPoints(ring);
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
    const [xi, yi] = points[i]!;
    const [xj, yj] = points[j]!;
    const intersects = yi > point[1] !== yj > point[1] && point[0] < ((xj - xi) * (point[1] - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

/**
 * Segments consécutifs d'une polyligne OUVERTE (`shapeKind: "line"`, voir `Shape.coordinates`) —
 * PAS de segment de "fermeture" retour du dernier point vers le premier, contrairement à un ring
 * fermé (polygone/rectangle/cercle développé) : une ligne agit comme un mur fin, pas comme le
 * contour d'une aire.
 */
function openSegments(points: LngLat[]): [LngLat, LngLat][] {
  const segments: [LngLat, LngLat][] = [];
  for (let i = 0; i < points.length - 1; i++) segments.push([points[i]!, points[i + 1]!]);
  return segments;
}

/**
 * Vrai si le segment `[p1, p2]` traverse le bord d'une forme de `obstacles` — `findRoute` y passe
 * les formes NON `walkable` (voir CLAUDE.md "Itinéraire / pathfinding" : `walkable` ne délimite pas
 * un réseau exclusif, il marque juste une forme comme ne bloquant PAS le tracé, exactement comme un
 * espace vide sans aucune forme — l'inverse aurait été plus intuitif à nommer mais correspond à ce
 * qui a été demandé). `excludeShapeIds` : les 2 extrémités du segment elles-mêmes (leur propre bord
 * croiserait sinon toujours ce test, la ligne partant précisément de leur centre).
 *
 * `shapeKind: "line"` (voir CLAUDE.md "Itinéraire / pathfinding") : traitée comme un mur fin (ses
 * segments consécutifs, voir `openSegments`), PAS comme le contour d'une aire fermée — une ligne à
 * moins de 2 points (dégénérée) est ignorée, comme un ring à moins de 3 sommets.
 */
function crossesObstacle(p1: LngLat, p2: LngLat, obstacles: Shape[], excludeShapeIds: Set<string>): boolean {
  for (const obstacle of obstacles) {
    if (excludeShapeIds.has(obstacle.id)) continue;
    if (obstacle.shapeKind === "line") {
      for (const [a, b] of openSegments(obstacle.coordinates)) {
        if (segmentsIntersect(p1, p2, a, b)) return true;
      }
      continue;
    }
    const ring = ringPoints(obstacleRing(obstacle));
    if (ring.length < 3) continue;
    for (let i = 0; i < ring.length; i++) {
      if (segmentsIntersect(p1, p2, ring[i]!, ring[(i + 1) % ring.length]!)) return true;
    }
  }
  return false;
}

/** Nombre de cellules maximum par axe pour la grille de repli `gridRoute` quand rien de plus fin
 * n'est nécessaire (voir `computeAdaptiveCellSize` ci-dessous, qui peut choisir une cellule plus
 * petite si la densité locale des obstacles l'exige) — borne le pire cas de calcul (au carré, donc
 * large marge) pour une zone sans obstacle particulièrement petit. Choisi assez fin pour qu'une
 * cellule reste nettement plus petite qu'un stand/couloir réel même sur une zone de recherche large
 * (voir `GRID_MARGIN_FACTORS`) : à 48, une cellule dépassait largement 100m sur un vrai plan
 * d'exposition, rendant la grille incapable de distinguer un stand d'un couloir — trouvé en testant
 * directement sur les données réelles (`exemple-v2.json`), pas anticipé à l'écriture initiale. */
const GRID_MAX_CELLS_PER_AXIS = 200;

/**
 * Budget total de cellules (lignes × colonnes) de la grille de repli — plafond réel du coût de
 * calcul (précalcul des cellules bloquées + A*), indépendant de `GRID_MAX_CELLS_PER_AXIS` : celui-ci
 * ne borne qu'un côté à la fois, alors que `computeAdaptiveCellSize` peut vouloir une cellule plus
 * petite sur les 2 axes en même temps quand la zone contient de très petits obstacles (voir CLAUDE.md
 * "Itinéraire / pathfinding" : un stand de quelques mètres collé à ses voisins, sur un plan où
 * départ/arrivée sont espacés de plusieurs centaines de mètres — `GRID_MAX_CELLS_PER_AXIS` seul y
 * imposerait une résolution bien plus grande que ce stand, le faisant disparaître de la grille au
 * même titre qu'un couloir trop étroit). Plus généreux que `GRID_MAX_CELLS_PER_AXIS` au carré
 * (200 × 200 = 40 000) : valeur choisie en mesurant directement sur les données réelles
 * (`exemple-v2.json`, ~1100 obstacles non walkable) — le plus petit budget qui retrouve encore un
 * itinéraire pour le cas qui a motivé ce module (2 stands séparés d'environ 560m, l'un d'eux collé à
 * ses voisins sans le moindre espace), avec une marge de sécurité (200 000 suffisait déjà tout
 * juste). Le coût PIRE CAS — aucun itinéraire trouvable, les 3 marges de `GRID_MARGIN_FACTORS`
 * tentées en entier — reste de l'ordre de la seconde sur ce même jeu de données : accepté comme le
 * prix d'une résolution fine seulement quand la densité locale l'exige (voir
 * `computeAdaptiveCellSize`), jamais payé sur le cas courant (une ligne droite directe ou un premier
 * essai de grille suffit dans l'immense majorité des trajets réels). */
const GRID_MAX_TOTAL_CELLS = 220_000;

/**
 * Nombre minimum de cellules qu'on veut voir traverser le plus petit obstacle "typique" du
 * voisinage — 1 seule cellule ferait disparaître un couloir plus étroit que lui de la grille (voir
 * `GRID_MAX_CELLS_PER_AXIS` ci-dessus) ; 3 laisse une marge pour qu'un couloir adjacent à ce même
 * obstacle reste, lui aussi, représenté par au moins une cellule libre.
 */
const CELLS_ACROSS_SMALLEST_OBSTACLE = 3;

/**
 * Centile (pas le minimum brut, ni la moyenne) utilisé pour choisir "le plus petit obstacle typique"
 * du voisinage (voir `computeAdaptiveCellSize`) — robuste à une unique forme dégénérée/anormalement
 * fine (bord d'un polygone à peine ouvert, artefact de saisie) qui imposerait sinon une résolution
 * absurde à toute la zone de recherche à cause d'un seul cas isolé.
 */
const OBSTACLE_SIZE_PERCENTILE = 0.1;

/**
 * Choisit la taille de cellule de la grille de repli `gridRoute` en fonction de la densité RÉELLE
 * des obstacles à proximité, plutôt que de la seule distance départ-arrivée
 * (`Math.max(width, height) / GRID_MAX_CELLS_PER_AXIS`, formule historique, reste le plafond "pas
 * plus fin que nécessaire" quand aucun petit obstacle ne le justifie) — voir CLAUDE.md "Itinéraire /
 * pathfinding" : sur un vrai plan d'exposition, une zone de recherche large (2 stands éloignés l'un
 * de l'autre) peut malgré tout contenir des stands minuscules collés à leurs voisins, que la seule
 * formule historique rendrait invisibles (une cellule plus grande que le stand lui-même le
 * recouvrirait ainsi que ses voisins, le marquant bloqué en entier). Ne descend cependant jamais
 * sous `budgetFloor` (déduit de `GRID_MAX_TOTAL_CELLS`, voir sa doc) : mieux vaut une résolution
 * insuffisante mais bornée qu'une grille dont le coût dépend, sans limite, de la taille du plus petit
 * obstacle rencontré n'importe où sur un grand plan.
 */
function computeAdaptiveCellSize(width: number, height: number, relevantObstacles: Shape[]): number {
  const distanceBasedCellSize = Math.max(width, height) / GRID_MAX_CELLS_PER_AXIS;
  const budgetFloor = Math.sqrt((width * height) / GRID_MAX_TOTAL_CELLS);

  const sizes = relevantObstacles
    .filter((o) => o.shapeKind !== "line")
    .map((o) => {
      const ring = obstacleRing(o);
      const xs = ring.map((p) => p[0]);
      const ys = ring.map((p) => p[1]);
      return Math.min(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys));
    })
    .filter((size) => size > 0)
    .sort((a, b) => a - b);

  if (sizes.length === 0) return Math.max(distanceBasedCellSize, budgetFloor);

  const idealCellSize = sizes[Math.floor(sizes.length * OBSTACLE_SIZE_PERCENTILE)]! / CELLS_ACROSS_SMALLEST_OBSTACLE;
  return Math.max(Math.min(idealCellSize, distanceBasedCellSize), budgetFloor);
}

/** Rectangle englobant, en coordonnées lng/lat — délimite la zone quadrillée par `gridRoute`. */
interface BoundingBox {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/**
 * Repli local : contourne les obstacles à l'intérieur du rectangle `bounds` quand la ligne droite
 * entre `start` et `end` est bloquée — appelé par `findRoute` seulement en DERNIER RECOURS, une fois
 * la ligne droite directe rejetée (voir `crossesObstacle`) : un plan dense (des dizaines de formes
 * NON walkable entre les deux) rend une ligne droite entre 2 points éloignés souvent bloquée par une
 * forme sur le chemin, alors qu'un vrai passage existe en zigzaguant entre les espaces libres —
 * exactement ce que `crossesObstacle` seul ne peut pas représenter (il ne sait dire que "bloqué" ou
 * "libre", jamais "contourner"). Rappel (voir CLAUDE.md) : l'espace est walkable PAR DÉFAUT, y
 * compris le vide sans aucune forme — seules les formes NON `walkable` de `obstacles` bloquent.
 *
 * Quadrille `bounds` en une grille régulière (résolution adaptative, voir `computeAdaptiveCellSize`),
 * marque une cellule "libre" si elle ne recouvre aucun `obstacle` (`excludeShapeIds` : les 2 extrémités
 * elles-mêmes, jamais des obstacles pour elles-mêmes) — SAUF la cellule qui contient exactement
 * `start`/`end` : toujours forcée libre, quel que soit le résultat de ce test sur elle (voir
 * `blocked.delete(startIndex)`/`blocked.delete(endIndex)` ci-dessous). Nécessaire même avec la
 * résolution adaptative : `start`/`end` sont des points exacts appartenant à leur propre forme
 * (toujours exclue d'`obstacles`), donc jamais eux-mêmes un obstacle — mais rien n'empêche qu'un
 * voisin authentiquement bloquant partage exactement la même cellule (2 formes posées bord à bord,
 * sans le moindre espace entre elles, voir CLAUDE.md "Itinéraire / pathfinding") ; cette correction ne
 * change QUE ce test de départ/arrivée, `blocked` reste inchangé pour le reste de la grille — un
 * voisin réellement bloquant le reste pour tout le reste de l'A* qui suit. Cherche ensuite le plus
 * court chemin par A* (8 directions, coins interdits en diagonale entre 2 cellules bloquées), puis
 * simplifie le résultat par "string pulling" (relie directement les points les plus éloignés tant
 * que la ligne entre eux reste dégagée, voir `crossesObstacle`) pour ne garder que les vrais
 * changements de direction plutôt qu'un escalier de cellules.
 *
 * Retourne les points INTERMÉDIAIRES uniquement (`start`/`end` déjà connus de l'appelant), ou `null`
 * si `start`/`end` tombent hors de `bounds`, ou si aucun chemin n'existe même en grille (obstacles
 * qui bouchent complètement la zone).
 */
/**
 * Tas binaire minimal (min-heap) pour l'A* de `gridRoute` — nécessaire à la résolution de grille
 * utilisée (`GRID_MAX_CELLS_PER_AXIS`, jusqu'à plusieurs dizaines de milliers de cellules) : une
 * sélection linéaire du meilleur nœud non visité (O(V²)) y devient mesurable (plusieurs secondes,
 * constaté en testant directement sur les données réelles). Suppression paresseuse des entrées
 * obsolètes (une meilleure distance vers un nœud déjà présent dans le tas) : l'appelant les ignore
 * via `visited` au lieu de les ré-indexer, plus simple et sans coût significatif ici.
 */
function createMinHeap(): { push: (index: number, priority: number) => void; pop: () => number | null } {
  const heap: { index: number; priority: number }[] = [];
  const swap = (i: number, j: number): void => {
    const tmp = heap[i]!;
    heap[i] = heap[j]!;
    heap[j] = tmp;
  };
  return {
    push(index, priority) {
      heap.push({ index, priority });
      let i = heap.length - 1;
      while (i > 0) {
        const parent = (i - 1) >> 1;
        if (heap[parent]!.priority <= heap[i]!.priority) break;
        swap(parent, i);
        i = parent;
      }
    },
    pop() {
      if (heap.length === 0) return null;
      const top = heap[0]!.index;
      const last = heap.pop()!;
      if (heap.length > 0) {
        heap[0] = last;
        let i = 0;
        while (true) {
          const left = 2 * i + 1;
          const right = 2 * i + 2;
          let smallest = i;
          if (left < heap.length && heap[left]!.priority < heap[smallest]!.priority) smallest = left;
          if (right < heap.length && heap[right]!.priority < heap[smallest]!.priority) smallest = right;
          if (smallest === i) break;
          swap(smallest, i);
          i = smallest;
        }
      }
      return top;
    },
  };
}

/** Vrai si le rectangle englobant de `ring` chevauche `bounds`, ne serait-ce que partiellement — un
 * simple test AABB, utilisé par `gridRoute` pour écarter au plus vite les obstacles hors de la zone
 * quadrillée avant le test de recouvrement cellule-par-cellule, bien plus coûteux (voir
 * `cellOverlapsRing`) sur un plan qui peut compter des centaines de formes non walkable au total. */
function boundingBoxesOverlap(bounds: BoundingBox, ring: LngLat[]): boolean {
  const xs = ring.map((p) => p[0]);
  const ys = ring.map((p) => p[1]);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  return !(maxX < bounds.minX || minX > bounds.maxX || maxY < bounds.minY || minY > bounds.maxY);
}

/**
 * Vrai si le rectangle `[minX,minY]`-`[maxX,maxY]` chevauche `ring`, ne serait-ce que partiellement
 * — contrairement à un simple échantillonnage du CENTRE de la cellule (voir `gridRoute`), ce test
 * complet (arêtes qui se croisent, coin du rectangle dans l'obstacle, obstacle entièrement contenu
 * dans le rectangle) ne peut jamais laisser passer un mur plus fin que la résolution de la grille :
 * un mur dont l'épaisseur tombe entre 2 centres de cellules resterait sinon complètement invisible.
 */
function cellOverlapsRing(minX: number, minY: number, maxX: number, maxY: number, ring: LngLat[]): boolean {
  const rect: LngLat[] = [
    [minX, minY],
    [maxX, minY],
    [maxX, maxY],
    [minX, maxY],
  ];
  const points = ringPoints(ring);
  if (points.length < 3) return false;
  for (let i = 0; i < 4; i++) {
    const a1 = rect[i]!;
    const a2 = rect[(i + 1) % 4]!;
    for (let j = 0; j < points.length; j++) {
      if (segmentsIntersect(a1, a2, points[j]!, points[(j + 1) % points.length]!)) return true;
    }
  }
  if (pointInPolygon(rect[0]!, points)) return true;
  if (pointInPolygon(points[0]!, rect)) return true;
  return false;
}

/**
 * Équivalent de `cellOverlapsRing` pour une polyligne OUVERTE (`shapeKind: "line"`) — une ligne n'a
 * pas d'intérieur, donc pas de test "le coin du rectangle est dans l'obstacle"/"l'obstacle est
 * entièrement dans le rectangle" (`pointInPolygon` n'a de sens que pour une aire fermée) : le
 * recouvrement se limite à "un segment de la ligne traverse un bord de la cellule" (un segment qui
 * TRAVERSE la cellule croise forcément un de ses bords) OU "un sommet de la ligne tombe dans la
 * cellule" (couvre le cas d'un segment entièrement contenu dans une seule cellule, trop court pour
 * en croiser un bord).
 */
function cellOverlapsOpenPolyline(minX: number, minY: number, maxX: number, maxY: number, points: LngLat[]): boolean {
  if (points.length < 2) return false;
  const rect: LngLat[] = [
    [minX, minY],
    [maxX, minY],
    [maxX, maxY],
    [minX, maxY],
  ];
  for (const [a, b] of openSegments(points)) {
    for (let i = 0; i < 4; i++) {
      if (segmentsIntersect(rect[i]!, rect[(i + 1) % 4]!, a, b)) return true;
    }
  }
  return points.some(([x, y]) => x >= minX && x <= maxX && y >= minY && y <= maxY);
}

/**
 * Poids de coût de traversée appliqué par `gridRoute` aux cellules à proximité d'une forme (n'importe
 * quel `shapeKind` — ligne, polygone, rectangle, cercle) porteuse de `Shape.pathPriority` — plus bas
 * que 1 = moins cher à traverser que de l'espace neutre, donc préféré par l'A* pondéré (voir
 * `cellWeight` dans `gridRoute`). Formule continue (pas une table à 3 paliers nommés) depuis que
 * `Shape.pathPriority` est numérique : décroît quand `priority` augmente, sans jamais atteindre 0
 * (une préférence, jamais un passage obligatoire) — `priority` négatif ou nul traité comme 0 (aucun
 * rabais, poids 1).
 */
export function pathPriorityWeight(priority: number): number {
  return 1 / (1 + Math.max(0, priority));
}

/**
 * Grille (résolution adaptative, voir `computeAdaptiveCellSize`) + cellules bloquées/à prix réduit
 * pour `bounds` — partagée par `gridRoute` (qui l'utilise pour son A*) et `computeAreaDebugGrid`
 * (qui n'a besoin QUE de ce résultat, sans A* : voir `MapAdapter.showPathfindingDebugGrid`/le bouton
 * show/hide de l'éditeur). Une seule implémentation plutôt que deux copies qui pourraient diverger
 * silencieusement (ex. si `computeAdaptiveCellSize` change un jour) — voir CLAUDE.md.
 * `excludeShapeIds` : optionnel, aucune forme exclue par défaut (pas de notion de départ/arrivée à
 * exclure pour une grille "toute la zone visible", contrairement à un trajet précis).
 */
function buildCellGrid(
  bounds: BoundingBox,
  obstacles: Shape[],
  influenceSources: Shape[],
  excludeShapeIds: Set<string> = new Set(),
) {
  const { minX, minY, maxX, maxY } = bounds;
  const width = maxX - minX;
  const height = maxY - minY;
  if (width <= 0 || height <= 0) return null;

  const relevantObstacles = obstacles.filter((o) => !excludeShapeIds.has(o.id) && boundingBoxesOverlap(bounds, obstacleRing(o)));
  const cellSize = computeAdaptiveCellSize(width, height, relevantObstacles);
  const cols = Math.max(1, Math.ceil(width / cellSize));
  const rows = Math.max(1, Math.ceil(height / cellSize));

  const rowOf = (index: number): number => Math.floor(index / cols);
  const colOf = (index: number): number => index % cols;
  const indexOf = (row: number, col: number): number => row * cols + col;
  const cellCenter = (row: number, col: number): LngLat => [minX + (col + 0.5) * cellSize, minY + (row + 0.5) * cellSize];
  /** Contour rectangulaire d'une cellule, pour le mode debug (`PathfindingDebugGrid`) — jamais
   * utilisé par l'algorithme lui-même (qui ne travaille qu'avec des indices/centres), seulement pour
   * visualiser après coup quelles cellules ont été bloquées/à prix réduit. */
  const cellRing = (index: number): LngLat[] => {
    const row = rowOf(index);
    const col = colOf(index);
    const cMinX = minX + col * cellSize;
    const cMinY = minY + row * cellSize;
    const cMaxX = cMinX + cellSize;
    const cMaxY = cMinY + cellSize;
    return [[cMinX, cMinY], [cMaxX, cMinY], [cMaxX, cMaxY], [cMinX, cMaxY], [cMinX, cMinY]];
  };

  /**
   * Cellules bloquées, précalculées une seule fois — pour CHAQUE obstacle (pas pour chaque cellule),
   * ne teste que les cellules que son propre rectangle englobant peut couvrir (voir `colStart`/
   * `colEnd`/`rowStart`/`rowEnd` ci-dessous), au lieu de tester chaque cellule contre tous les
   * obstacles. Sur un vrai plan dense (~1000 formes non walkable) avec une grande zone de recherche,
   * la seconde approche redevenait mesurable (plusieurs secondes, constaté en testant directement sur
   * les données réelles) — chaque obstacle ne couvre en réalité qu'une poignée de cellules, jamais la
   * grille entière.
   */
  const blocked = new Set<number>();
  for (const obstacle of relevantObstacles) {
    const ring = obstacleRing(obstacle);
    const oxs = ring.map((p) => p[0]);
    const oys = ring.map((p) => p[1]);
    const colStart = Math.max(0, Math.floor((Math.max(minX, Math.min(...oxs)) - minX) / cellSize));
    const colEnd = Math.min(cols - 1, Math.floor((Math.min(maxX, Math.max(...oxs)) - minX) / cellSize));
    const rowStart = Math.max(0, Math.floor((Math.max(minY, Math.min(...oys)) - minY) / cellSize));
    const rowEnd = Math.min(rows - 1, Math.floor((Math.min(maxY, Math.max(...oys)) - minY) / cellSize));
    for (let row = rowStart; row <= rowEnd; row++) {
      for (let col = colStart; col <= colEnd; col++) {
        const index = indexOf(row, col);
        if (blocked.has(index)) continue;
        const cMinX = minX + col * cellSize;
        const cMinY = minY + row * cellSize;
        const overlaps =
          obstacle.shapeKind === "line"
            ? cellOverlapsOpenPolyline(cMinX, cMinY, cMinX + cellSize, cMinY + cellSize, obstacle.coordinates)
            : cellOverlapsRing(cMinX, cMinY, cMinX + cellSize, cMinY + cellSize, ring);
        if (overlaps) blocked.add(index);
      }
    }
  }

  /**
   * Rabais de coût par cellule (voir `pathPriorityWeight`) — un simple biais, jamais un blocage
   * (contrairement à `blocked` ci-dessus, qui prime toujours : une cellule bloquée n'est simplement
   * jamais visitée par l'A*, `cellWeight` n'a alors plus d'importance pour elle). C'est suffisant
   * pour que l'A* pondéré préfère naturellement ces cellules (moins chères) à un détour équivalent
   * en terrain neutre, sans avoir besoin d'un rayon séparé à calibrer.
   */
  const relevantInfluenceSources = influenceSources.filter(
    (s) => !excludeShapeIds.has(s.id) && boundingBoxesOverlap(bounds, obstacleRing(s)),
  );
  const cellWeight = new Map<number, number>();
  for (const source of relevantInfluenceSources) {
    const weight = pathPriorityWeight(source.pathPriority!);
    // `obstacleRing` (pas `source.coordinates` directement) pour englober correctement un cercle
    // (dont `coordinates` n'est que `[centre, point de bord]`, pas son vrai contour) — identique à
    // `source.coordinates` pour les autres `shapeKind`.
    const ring = obstacleRing(source);
    const sxs = ring.map((p) => p[0]);
    const sys = ring.map((p) => p[1]);
    // Précalculé une seule fois par source d'influence, restreint à son propre rectangle englobant
    // (aucune marge) — même optimisation que `blocked` ci-dessus.
    const colStart = Math.max(0, Math.floor((Math.max(minX, Math.min(...sxs)) - minX) / cellSize));
    const colEnd = Math.min(cols - 1, Math.floor((Math.min(maxX, Math.max(...sxs)) - minX) / cellSize));
    const rowStart = Math.max(0, Math.floor((Math.max(minY, Math.min(...sys)) - minY) / cellSize));
    const rowEnd = Math.min(rows - 1, Math.floor((Math.min(maxY, Math.max(...sys)) - minY) / cellSize));

    // Une forme WALKABLE (pas une ligne, qui n'a de toute façon pas d'intérieur) n'est jamais un
    // obstacle : la traverser est déjà légitime, donc le rabais s'applique à toute son aire (pas
    // seulement son contour) — une place/zone piétonne attractive doit inciter à la traverser, pas
    // seulement à en longer le bord.
    const discountsInterior = source.shapeKind !== "line" && !!source.walkable;
    if (discountsInterior) {
      for (let row = rowStart; row <= rowEnd; row++) {
        for (let col = colStart; col <= colEnd; col++) {
          const index = indexOf(row, col);
          if ((cellWeight.get(index) ?? 1) <= weight) continue;
          if (blocked.has(index)) continue;
          const cMinX = minX + col * cellSize;
          const cMinY = minY + row * cellSize;
          if (cellOverlapsRing(cMinX, cMinY, cMinX + cellSize, cMinY + cellSize, ring)) {
            cellWeight.set(index, weight);
          }
        }
      }
      continue;
    }

    // Ligne WALKABLE : jamais un obstacle (`blocked` reste vide pour elle), donc aucun conflit
    // possible avec `blocked` — la simple cellule qui touche le tracé EST déjà la bonne cellule à
    // longer, pas besoin d'aller chercher plus loin. Reste donc une seule passe, sans dilatation
    // (voir plus bas) : l'ajouter ici épaississait inutilement la bande à prix réduit tout autour
    // d'un simple chemin piéton attractif, alors qu'elle rendait déjà bien avant ce correctif.
    if (source.walkable) {
      for (let row = rowStart; row <= rowEnd; row++) {
        for (let col = colStart; col <= colEnd; col++) {
          const index = indexOf(row, col);
          if ((cellWeight.get(index) ?? 1) <= weight) continue;
          const cMinX = minX + col * cellSize;
          const cMinY = minY + row * cellSize;
          if (cellOverlapsOpenPolyline(cMinX, cMinY, cMinX + cellSize, cMinY + cellSize, ring)) {
            cellWeight.set(index, weight);
          }
        }
      }
      continue;
    }

    // Ligne, ou forme NON walkable (mur attractif) : le rabais doit rester une incitation à longer
    // le CONTOUR, pas à traverser l'aire (déjà bloquée/inatteignable pour une forme non walkable —
    // lui donner AUSSI un rabais n'aurait aucun sens, l'A* ne la visite jamais). Problème : une
    // cellule qui recouvre RÉELLEMENT le contour recouvre quasiment toujours AUSSI un peu d'intérieur
    // (le contour n'a pas d'épaisseur — toute cellule qui le touche a nécessairement une aire de part
    // et d'autre) — pour une forme non walkable, cette même cellule est donc TOUJOURS `blocked` aussi
    // (voir `blocked` ci-dessus, même test de recouvrement). En excluant simplement les cellules
    // bloquées de ce rabais (nécessaire, voir plus bas), plus AUCUNE cellule ne recevait jamais de
    // rabais pour ce cas — constaté en testant avec un mur non walkable isolé, zéro cellule à prix
    // réduit dans le résultat. Résolu en 2 passes, UNIQUEMENT pour ce cas non walkable (voir
    // ci-dessus pour la ligne walkable, qui n'en a pas besoin) : (1) repérer les cellules qui
    // touchent le contour (`contourIndices`, sans se soucier d'être bloquées ou non — juste des
    // candidates), (2) étendre ("dilater") aux 8 voisines de CHACUNE d'elles — ce sont ELLES, juste
    // à côté d'une cellule touchant le mur, qui sont les vraies cellules LIBRES longeant son contour
    // depuis l'extérieur. Seules les cellules non bloquées de cet ensemble élargi reçoivent
    // finalement le rabais.
    const contourIndices: number[] = [];
    for (let row = rowStart; row <= rowEnd; row++) {
      for (let col = colStart; col <= colEnd; col++) {
        const cMinX = minX + col * cellSize;
        const cMinY = minY + row * cellSize;
        if (cellOverlapsOpenPolyline(cMinX, cMinY, cMinX + cellSize, cMinY + cellSize, ring)) {
          contourIndices.push(indexOf(row, col));
        }
      }
    }
    const candidateIndices = new Set(contourIndices);
    for (const index of contourIndices) {
      const row = rowOf(index);
      const col = colOf(index);
      for (let dr = -1; dr <= 1; dr++) {
        for (let dc = -1; dc <= 1; dc++) {
          const nr = row + dr;
          const nc = col + dc;
          if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
          candidateIndices.add(indexOf(nr, nc));
        }
      }
    }
    for (const index of candidateIndices) {
      if (blocked.has(index)) continue;
      if ((cellWeight.get(index) ?? 1) <= weight) continue;
      cellWeight.set(index, weight);
    }
  }

  return { cellSize, cols, rows, rowOf, colOf, indexOf, cellCenter, cellRing, blocked, cellWeight, relevantObstacles, relevantInfluenceSources };
}

/** Grille de debug pour `bounds` seule (voir `PathfindingDebugGrid`/`buildCellGrid`) — SANS notion
 * de départ/arrivée, contrairement à `findRouteDebugGrid` : pensée pour un bouton show/hide "toute la
 * zone visible" dans l'éditeur, pas pour diagnostiquer un trajet précis. `null` si `bounds` est
 * dégénéré (largeur/hauteur nulle ou négative). */
export function computeAreaDebugGrid(shapes: Shape[], bounds: BoundingBox): PathfindingDebugGrid | null {
  const obstacles = shapes.filter((s) => !s.walkable);
  const influenceSources = shapes.filter((s) => s.pathPriority !== undefined);
  const grid = buildCellGrid(bounds, obstacles, influenceSources);
  if (!grid) return null;
  return {
    bounds,
    cellSize: grid.cellSize,
    blockedCells: Array.from(grid.blocked, grid.cellRing),
    cheapCells: Array.from(grid.cellWeight, ([index, weight]) => ({ ring: grid.cellRing(index), weight })),
  };
}

function gridRoute(
  bounds: BoundingBox,
  obstacles: Shape[],
  influenceSources: Shape[],
  start: LngLat,
  end: LngLat,
  excludeShapeIds: Set<string>,
): { waypoints: LngLat[]; cost: number; debugGrid: PathfindingDebugGrid } | null {
  const grid = buildCellGrid(bounds, obstacles, influenceSources, excludeShapeIds);
  if (!grid) return null;
  const { minX, minY } = bounds;
  const {
    cellSize, cols, rows, rowOf, colOf, indexOf, cellCenter, cellRing, blocked, cellWeight,
    relevantObstacles, relevantInfluenceSources,
  } = grid;

  const isFree = (row: number, col: number): boolean => {
    if (row < 0 || row >= rows || col < 0 || col >= cols) return false;
    return !blocked.has(indexOf(row, col));
  };
  const toCell = (p: LngLat): [number, number] => [
    Math.min(rows - 1, Math.max(0, Math.floor((p[1] - minY) / cellSize))),
    Math.min(cols - 1, Math.max(0, Math.floor((p[0] - minX) / cellSize))),
  ];
  const hasInfluence = cellWeight.size > 0;
  /** Échantillonne le segment droit `[a, b]` par pas de le QUART de la taille d'une cellule (pas
   * `cellSize` entier) et somme `pas × cellWeight(cellule)` — sert à décider si un raccourci du
   * "string pulling" (voir plus bas) profite réellement du rabais de coût, plutôt que de l'effacer
   * aveuglément. Un pas de `cellSize` entier ratait parfois une bande de cellules à prix réduit fine
   * (celles qui recouvrent seulement le tracé d'une source, voir `cellWeight` ci-dessus — une seule
   * rangée de cellules, pas une zone épaisse) quand le segment testé la traverse en biais : selon
   * l'alignement exact des points échantillonnés, l'estimation pouvait la manquer complètement OU au
   * contraire l'attraper sur tout un pas alors que le segment ne l'effleurait qu'un instant —
   * produisant un accept/reject du raccourci instable, sensible à un déplacement de quelques mètres
   * de `from`/`to` sans rapport avec le coût réel (constaté en testant : la préférence s'activait ou
   * disparaissait totalement selon la position exacte d'une forme, plutôt que proportionnellement à
   * l'ampleur du détour). Un pas plus fin réduit ce risque d'aliasing sans changer le comportement
   * pour un segment qui ne croise aucune cellule à prix réduit (chaque pas reste alors à poids 1).
   */
  const estimateWeightedSegmentLength = (a: LngLat, b: LngLat): number => {
    const segmentLength = distance(a, b);
    if (segmentLength === 0) return 0;
    const steps = Math.max(1, Math.ceil(segmentLength / (cellSize / 4)));
    let total = 0;
    for (let s = 0; s < steps; s++) {
      const t0 = s / steps;
      const t1 = (s + 1) / steps;
      const midT = (t0 + t1) / 2;
      const mid: LngLat = [a[0] + (b[0] - a[0]) * midT, a[1] + (b[1] - a[1]) * midT];
      const [row, col] = toCell(mid);
      const weight = cellWeight.get(indexOf(row, col)) ?? 1;
      total += segmentLength * (t1 - t0) * weight;
    }
    return total;
  };

  const [startRow, startCol] = toCell(start);
  const [endRow, endCol] = toCell(end);
  const startIndex = indexOf(startRow, startCol);
  const endIndex = indexOf(endRow, endCol);
  // `start`/`end` sont des points exacts appartenant à leur propre forme, déjà exclue d'`obstacles`
  // (voir `excludeShapeIds`) — jamais un obstacle pour eux-mêmes. Une cellule plus grande que cette
  // forme peut néanmoins recouvrir aussi un voisin et se retrouver marquée bloquée dans son ensemble
  // (voir CLAUDE.md : un petit stand collé à ses voisins) ; ça ne concerne alors QUE ce test de départ
  // — `blocked` reste inchangé pour l'A* qui suit, donc un voisin réellement bloquant continue de
  // l'être pour tout le reste de la recherche.
  blocked.delete(startIndex);
  blocked.delete(endIndex);

  // A* avec tas binaire (voir `createMinHeap`) — nécessaire à cette résolution
  // (`GRID_MAX_CELLS_PER_AXIS`) : une sélection linéaire du meilleur nœud non visité (O(V²)) devient
  // mesurable (plusieurs secondes) sur une grille de plusieurs dizaines de milliers de cellules.
  // Admissibilité de l'heuristique (voir `cellWeight` ci-dessus) : dès qu'au moins une cellule coûte
  // MOINS que `cellSize` (rabais `pathPriorityWeight`), une heuristique purement géométrique
  // surestimerait le coût restant dans cette zone — un nœud pourrait alors être "fermé" (`visited`,
  // jamais rouvert) avec un coût sous-optimal avant que l'A* n'ait fini d'explorer le corridor bon
  // marché, empêchant le rabais de jamais faire dévier le tracé. Mise à l'échelle par le poids minimal
  // RÉELLEMENT PRÉSENT parmi `relevantInfluenceSources` pour CET appel (borne inférieure garantie du
  // coût réel d'une arête) — jamais appliquée sans source d'influence pertinente, pour ne pas
  // dégrader l'A* vers un Dijkstra plus lent sur le cas obstacle-only déjà finement calibré.
  const heuristicWeight = hasInfluence
    ? Math.min(...relevantInfluenceSources.map((s) => pathPriorityWeight(s.pathPriority!)))
    : 1;
  const heuristic = (index: number): number =>
    Math.hypot(rowOf(index) - endRow, colOf(index) - endCol) * cellSize * heuristicWeight;
  const dist = new Map<number, number>([[startIndex, 0]]);
  const previous = new Map<number, number>();
  const visited = new Set<number>();
  const NEIGHBOR_OFFSETS: [number, number][] = [
    [-1, 0], [1, 0], [0, -1], [0, 1],
    [-1, -1], [-1, 1], [1, -1], [1, 1],
  ];

  const heap = createMinHeap();
  heap.push(startIndex, heuristic(startIndex));

  while (true) {
    const currentIndex = heap.pop();
    if (currentIndex === null) break;
    // Entrée obsolète (une meilleure distance vers ce nœud a déjà été traitée depuis — voir
    // `createMinHeap`, suppression paresseuse plutôt que ré-indexation) : ignorée, pas une fin de
    // recherche.
    if (visited.has(currentIndex)) continue;
    if (currentIndex === endIndex) break;
    visited.add(currentIndex);
    const row = rowOf(currentIndex);
    const col = colOf(currentIndex);
    const currentDist = dist.get(currentIndex)!;

    for (const [dr, dc] of NEIGHBOR_OFFSETS) {
      const nr = row + dr;
      const nc = col + dc;
      if (!isFree(nr, nc)) continue;
      // Empêche de couper un coin en diagonale entre 2 cellules bloquées adjacentes.
      if (dr !== 0 && dc !== 0 && (!isFree(row + dr, col) || !isFree(row, col + dc))) continue;
      const neighborIndex = indexOf(nr, nc);
      if (visited.has(neighborIndex)) continue;
      const candidate = currentDist + Math.hypot(dr, dc) * cellSize * (cellWeight.get(neighborIndex) ?? 1);
      if (candidate < (dist.get(neighborIndex) ?? Infinity)) {
        dist.set(neighborIndex, candidate);
        previous.set(neighborIndex, currentIndex);
        heap.push(neighborIndex, candidate + heuristic(neighborIndex));
      }
    }
  }

  const endCost = dist.get(endIndex);
  if (!Number.isFinite(endCost ?? Infinity)) return null;

  const cellPathIndices: number[] = [endIndex];
  let cursor = endIndex;
  while (cursor !== startIndex) {
    const prev = previous.get(cursor);
    if (prev === undefined) return null;
    cellPathIndices.unshift(prev);
    cursor = prev;
  }
  const fullPath: LngLat[] = [start, ...cellPathIndices.map((index) => cellCenter(rowOf(index), colOf(index))), end];
  // Parallèle à `fullPath`, mêmes indices — donne accès à `dist` (coût pondéré optimal déjà connu,
  // voir la boucle A* ci-dessus) pour chaque point de `fullPath`, y compris `start`/`end` eux-mêmes
  // (`cellPathIndices` commence/finit déjà par `startIndex`/`endIndex`, voir la boucle juste
  // au-dessus — d'où la légère redondance assumée ici, sans incidence : `dist` donne la même valeur
  // pour un index répété).
  const pathIndices: number[] = [startIndex, ...cellPathIndices, endIndex];

  // "String pulling" : depuis chaque point, relie directement le plus loin possible tant que la
  // ligne reste dégagée — réduit l'escalier de cellules à quelques vrais coins. Sans source
  // d'influence (`!hasInfluence`), seul `crossesObstacle` décide (comportement historique inchangé).
  // Avec au moins une source pertinente pour CET appel, un raccourci n'est accepté que s'il ne coûte
  // pas plus cher (coût pondéré, pas juste géométrique) que le chemin qu'il remplace — sinon
  // `crossesObstacle` seul accepterait TOUJOURS le raccourci le plus direct dès que rien ne le bloque,
  // effaçant systématiquement l'infléchissement vers un chemin/mur attractif proche mais non bloquant
  // (le scénario même que `pathPriority` doit produire).
  const simplified: LngLat[] = [fullPath[0]!];
  let i = 0;
  while (i < fullPath.length - 1) {
    let farthest = i + 1;
    for (let j = fullPath.length - 1; j > i + 1; j--) {
      if (crossesObstacle(fullPath[i]!, fullPath[j]!, relevantObstacles, excludeShapeIds)) continue;
      if (hasInfluence) {
        const optimalCost = (dist.get(pathIndices[j]!) ?? Infinity) - (dist.get(pathIndices[i]!) ?? 0);
        const shortcutCost = estimateWeightedSegmentLength(fullPath[i]!, fullPath[j]!);
        if (shortcutCost > optimalCost * (1 + 1e-6)) continue;
      }
      farthest = j;
      break;
    }
    simplified.push(fullPath[farthest]!);
    i = farthest;
  }

  const debugGrid: PathfindingDebugGrid = {
    bounds,
    cellSize,
    blockedCells: Array.from(blocked, cellRing),
    cheapCells: Array.from(cellWeight, ([index, weight]) => ({ ring: cellRing(index), weight })),
  };
  return { waypoints: simplified.slice(1, -1), cost: endCost!, debugGrid };
}

/**
 * Point "centre" d'une forme pour l'itinéraire — `polygonCentroid` seule donnerait, pour un cercle
 * (`shape.coordinates` = exactement `[centre, point de bord]`, aire signée nulle), la simple MOYENNE
 * des 2 points, donc un point décalé du vrai centre (à michemin vers le bord), pas le centre réel.
 * `coordinates[0]` est déjà le centre exact pour un cercle (même lecture que `MapLibreAdapter`'s
 * `shapeCenter` pour le rendu) — pas besoin de calcul géométrique dans ce cas.
 */
/** Centre d'une forme, quelle que soit sa nature — le point par lequel un itinéraire l'atteint. */
export function shapeCentroid(shape: Shape): LngLat {
  if (shape.shapeKind === "circle") return shape.coordinates[0]!;
  return polygonCentroid(shape.coordinates);
}

function resolveEndpointPosition(endpoint: RouteEndpoint, shapesById: Map<string, Shape>): LngLat | null {
  if (endpoint.type === "position") return endpoint.position;
  const shape = shapesById.get(endpoint.shapeId);
  return shape ? shapeCentroid(shape) : null;
}

/**
 * Marges essayées successivement par `findRoute` pour la zone quadrillée par `gridRoute` (voir son
 * commentaire), en multiple de la distance directe départ-arrivée — la 1ère (assez généreuse pour un
 * simple contournement local) suffit dans l'immense majorité des cas ; les suivantes, plus larges, ne
 * sont tentées que si elle échoue (obstacles étendus, détour nécessaire plus loin). Volontairement
 * PETITES (pas de facteur proche de 1, encore moins au-delà) : une zone de recherche qui grandit vite
 * dilue d'autant la résolution de la grille (`GRID_MAX_CELLS_PER_AXIS` reste fixe) — un facteur trop
 * généreux dès la 1ère tentative avait rendu chaque stand invisible pour la grille sur un vrai plan
 * d'exposition (cellules de plus de 100m), constaté en testant directement sur les données réelles.
 */
const GRID_MARGIN_FACTORS = [0.3, 1, 3];

/**
 * Cherche un itinéraire en ligne directe entre `from` et `to` (CRS canonique, lng/lat) — voir
 * CLAUDE.md "Itinéraire / pathfinding" : `Shape.walkable` ne délimite PAS un réseau de formes par
 * lesquelles il faudrait obligatoirement passer. Le principe est inverse et plus simple : le tracé
 * peut passer PARTOUT sur le plan par défaut, y compris sur du vide sans aucune forme ou sur une
 * forme `walkable`, et seule une forme NON `walkable` agit comme un obstacle qui le bloque
 * (`crossesObstacle`) — exactement comme un vrai mur physique.
 *
 * Essaie d'abord la ligne droite directe entre les 2 extrémités ; si elle est bloquée par au moins
 * un obstacle, retombe sur `gridRoute` (quadrillage local + A*, voir son commentaire) pour contourner
 * en zigzaguant entre les espaces libres, avec une zone de recherche qui s'élargit progressivement
 * (voir `GRID_MARGIN_FACTORS`) si les tentatives précédentes, plus étroites, échouent. `null` si
 * `from`/`to` désignent une forme inconnue, ou si aucun chemin n'existe même avec la plus grande
 * marge (obstacles qui bouchent complètement la zone).
 *
 * La ligne droite n'est retournée immédiatement que si, EN PLUS de ne croiser aucun obstacle, aucune
 * forme à `Shape.pathPriority` (voir CLAUDE.md, `pathPriorityWeight`) ne passe à proximité — sinon
 * `gridRoute` tourne quand même (coût pondéré, voir son commentaire) pour lui laisser une chance de
 * s'en approcher. AUCUN plafond arbitraire sur la taille du détour accepté (pas de garde-fou du
 * genre "au-delà de X fois la distance directe, revenir à la ligne droite") : c'est le coût pondéré
 * lui-même, calculé par l'A* de `gridRoute`, qui décide seul si le détour en vaut la peine — un tel
 * plafond a été retiré délibérément (voir git log) après avoir constaté qu'il annulait la préférence
 * de façon abrupte et arbitraire dès qu'un détour dépassait le seuil, MÊME quand le rabais de coût
 * réel la rendait largement rentable (une préférence numériquement quasi gratuite, ex.
 * `pathPriority` extrême, pouvait tout de même être totalement ignorée).
 */
interface RouteContext {
  fromPosition: LngLat;
  toPosition: LngLat;
  shapeIds: string[];
  excludeShapeIds: Set<string>;
  obstacles: Shape[];
  influenceSources: Shape[];
  directBlocked: boolean;
  directDistance: number;
  hasNearbyInfluence: boolean;
}

/** Prépare tout ce qui est commun à `findRoute`/`findRouteDebugGrid` avant leur propre boucle de
 * marges (voir le commentaire de `findRoute`) — factorisé pour que les deux ne puissent pas diverger
 * sur CETTE partie (résolution des extrémités, obstacles/sources d'influence pertinentes, court-
 * circuit ligne droite). `null` si `from`/`to` désignent une forme inconnue. */
/**
 * La forme entoure-t-elle ce point ? Une polyligne (`shapeKind: "line"`) n'a pas d'intérieur : elle
 * n'entoure jamais rien, même refermée à l'œil. Un cercle passe par `obstacleRing`, comme partout
 * ailleurs ici.
 */
function shapeContainsPoint(shape: Shape, point: LngLat): boolean {
  if (shape.shapeKind === "line") return false;
  return pointInPolygon(point, obstacleRing(shape));
}

function resolveRouteContext(options: FindRouteOptions): RouteContext | null {
  const { shapes, from, to } = options;
  const shapesById = new Map(shapes.map((s) => [s.id, s] as const));
  const fromPosition = resolveEndpointPosition(from, shapesById);
  const toPosition = resolveEndpointPosition(to, shapesById);
  if (!fromPosition || !toPosition) return null;

  const shapeIds = [
    from.type === "shape" ? from.shapeId : null,
    to.type === "shape" ? to.shapeId : null,
  ].filter((id): id is string => id !== null);

  // Une extrémité donnée en position brute (ex. un visiteur géolocalisé) peut tomber À L'INTÉRIEUR
  // d'une forme : il est debout dans le stand qu'elle représente, situation ordinaire et non cas
  // limite. Sans exemption il y serait enfermé — la ligne droite croise forcément le bord de cette
  // forme en sortant, et la grille de secours ne lui offre que des cellules bloquées autour de lui.
  // Même raison que l'exemption des formes-extrémités ci-dessous, pour le cas où l'extrémité n'est
  // pas une forme mais un point.
  const rawPositions = [
    from.type === "position" ? fromPosition : null,
    to.type === "position" ? toPosition : null,
  ].filter((position): position is LngLat => position !== null);
  const enclosingShapeIds = shapes
    .filter((shape) => rawPositions.some((position) => shapeContainsPoint(shape, position)))
    .map((shape) => shape.id);

  // Ne bloquent jamais la ligne : les formes `walkable`, les extrémités elles-mêmes (leur propre
  // bord croiserait sinon toujours le test, la ligne partant précisément de leur centre), et celles
  // qui entourent une extrémité donnée en position brute (voir ci-dessus).
  const excludeShapeIds = new Set([...shapeIds, ...enclosingShapeIds]);
  const obstacles = shapes.filter((s) => !s.walkable && !excludeShapeIds.has(s.id));
  // Formes "chemin"/"mur attractif" (voir `Shape.pathPriority`) — n'importe quel `shapeKind`
  // (ligne, polygone, rectangle, cercle), walkable OU non, peu importe : une forme NON walkable à
  // `pathPriority` reste par ailleurs dans `obstacles` ci-dessus (double rôle assumé, obstacle ET
  // source d'influence, sans conflit — `blocked` prime toujours sur `cellWeight` dans `gridRoute`).
  const influenceSources = shapes.filter(
    (s) => s.pathPriority !== undefined && !excludeShapeIds.has(s.id),
  );

  const directBlocked = crossesObstacle(fromPosition, toPosition, obstacles, excludeShapeIds);
  const directDistance = distance(fromPosition, toPosition);
  // Rayon de "pertinence" pour décider si une source d'influence mérite qu'on abandonne le
  // court-circuit ligne droite — PROPORTIONNEL au trajet (même logique que `GRID_MARGIN_FACTORS`) :
  // depuis que `cellWeight` (`gridRoute`) ne récompense plus que les cellules qui recouvrent
  // RÉELLEMENT le tracé d'une source (pas une zone élargie autour), il n'y a plus de rayon fixe à
  // utiliser comme plancher ici — seule la géométrie du trajet compte. Sans aucune forme à
  // `pathPriority` sur la carte, `hasNearbyInfluence` reste toujours faux (tableau vide) : le
  // court-circuit ligne droite ci-dessous reste alors inchangé, à coût nul.
  const nearbyTriggerRadius = directDistance * GRID_MARGIN_FACTORS[0]!;
  const directBounds: BoundingBox = {
    minX: Math.min(fromPosition[0], toPosition[0]) - nearbyTriggerRadius,
    maxX: Math.max(fromPosition[0], toPosition[0]) + nearbyTriggerRadius,
    minY: Math.min(fromPosition[1], toPosition[1]) - nearbyTriggerRadius,
    maxY: Math.max(fromPosition[1], toPosition[1]) + nearbyTriggerRadius,
  };
  // `obstacleRing` (pas `s.coordinates` directement) : un cercle n'a que `[centre, point de bord]`
  // comme `coordinates`, un bbox construit sur ces 2 points seuls sous-estimerait largement son
  // vrai contour.
  const hasNearbyInfluence = influenceSources.some((s) => boundingBoxesOverlap(directBounds, obstacleRing(s)));

  return { fromPosition, toPosition, shapeIds, excludeShapeIds, obstacles, influenceSources, directBlocked, directDistance, hasNearbyInfluence };
}

/** Essaie chaque marge de `GRID_MARGIN_FACTORS` (voir le commentaire de `findRoute`) et renvoie le
 * meilleur résultat de `gridRoute` — la première qui réussit sans source d'influence à proximité
 * (comportement historique), sinon celui de coût pondéré le plus bas parmi TOUTES les marges qui
 * réussissent (voir le commentaire de `findRoute` pour pourquoi la première ne suffit pas dans ce
 * cas). Partagé par `findRoute`/`findRouteDebugGrid`, qui ne diffèrent que par CE qu'ils gardent du
 * résultat (le tracé, ou la grille de debug). */
function bestGridRouteResult(
  context: RouteContext,
): { waypoints: LngLat[]; cost: number; debugGrid: PathfindingDebugGrid } | null {
  const { fromPosition, toPosition, excludeShapeIds, obstacles, influenceSources, directDistance, hasNearbyInfluence } = context;
  let best: { waypoints: LngLat[]; cost: number; debugGrid: PathfindingDebugGrid } | null = null;
  for (const marginFactor of GRID_MARGIN_FACTORS) {
    const margin = directDistance * marginFactor;
    const bounds: BoundingBox = {
      minX: Math.min(fromPosition[0], toPosition[0]) - margin,
      maxX: Math.max(fromPosition[0], toPosition[0]) + margin,
      minY: Math.min(fromPosition[1], toPosition[1]) - margin,
      maxY: Math.max(fromPosition[1], toPosition[1]) + margin,
    };
    const result = gridRoute(bounds, obstacles, influenceSources, fromPosition, toPosition, excludeShapeIds);
    if (!result) continue;

    if (!hasNearbyInfluence) return result;
    if (!best || result.cost < best.cost) best = result;
  }
  return best;
}

export function findRoute(options: FindRouteOptions): RouteResult | null {
  const { shapes, from, to } = options;

  if (from.type === "shape" && to.type === "shape" && from.shapeId === to.shapeId) {
    const shape = shapes.find((s) => s.id === from.shapeId);
    if (!shape) return null;
    const point = shapeCentroid(shape);
    return { coordinates: [point, point], shapeIds: [shape.id] };
  }

  const context = resolveRouteContext(options);
  if (!context) return null;
  const { fromPosition, toPosition, shapeIds, directBlocked, hasNearbyInfluence } = context;

  if (!directBlocked && !hasNearbyInfluence) {
    return { coordinates: [fromPosition, toPosition], shapeIds };
  }

  const best = bestGridRouteResult(context);
  if (!best) return null;
  return { coordinates: [fromPosition, ...best.waypoints, toPosition], shapeIds };
}

/**
 * Variante debug de `findRoute` (voir son commentaire, et `PathfindingDebugGrid`) — même résolution
 * d'extrémités/obstacles/sources d'influence, mais renvoie la grille effectivement explorée par
 * `gridRoute` pour la marge retenue plutôt que le tracé lui-même. `null` dans tous les cas où
 * `findRoute` n'aurait de toute façon jamais appelé `gridRoute` (formes de départ/arrivée identiques,
 * extrémité inconnue, ligne droite directe déjà suffisante) : rien à visualiser dans ces cas, il n'y
 * a simplement pas de grille construite. Pensée pour un usage ponctuel depuis la console
 * (`window.tokamap.mapAdapter.showPathfindingDebugGrid(...)`, voir `MapAdapter`), pas appelée par
 * `findRoute`/le calcul d'itinéraire réel — aucun coût pour le chemin normal.
 */
export function findRouteDebugGrid(options: FindRouteOptions): PathfindingDebugGrid | null {
  const { from, to } = options;
  if (from.type === "shape" && to.type === "shape" && from.shapeId === to.shapeId) return null;

  const context = resolveRouteContext(options);
  if (!context || (!context.directBlocked && !context.hasNearbyInfluence)) return null;

  return bestGridRouteResult(context)?.debugGrid ?? null;
}
